import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Trash2, Plus, Sparkles } from "lucide-react";

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

function pence(p: number) { return `£${(p / 100).toFixed(2)}`; }

type OfferType = "standard" | "special_price" | "percent";

type Upsell = {
  id: number;
  menuItemId: number;
  offerType: OfferType;
  specialPricePence: number | null;
  percentOff: number | null;
  sortOrder: number;
  active: boolean;
  name: string | null;
  imageUrl: string | null;
  normalPricePence: number | null;
  available: boolean | null;
  offerPricePence: number | null;
};

type MenuItem = { id: number; name: string; pricePence: number; imageUrl: string | null; available: boolean; hiddenFromMenu?: boolean };

/**
 * Shop config for checkout upsells — the "Fancy any of these?" offers shown to a
 * customer right before they pay. The shop features its own menu items at a special
 * checkout price (a fixed price or a % off). Server enforces the offer price and
 * rejects a special price above the item's normal price.
 */
export function CheckoutUpsellsManager({ storeId }: { storeId: number }) {
  const { toast } = useToast();
  const [upsells, setUpsells] = useState<Upsell[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Add-form state
  const [adding, setAdding] = useState(false);
  const [pickItemId, setPickItemId] = useState<number | "">("");
  const [offerType, setOfferType] = useState<OfferType>("special_price");
  const [priceInput, setPriceInput] = useState("");
  const [percentInput, setPercentInput] = useState("");
  const [hideFromMenu, setHideFromMenu] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [uRes, mRes] = await Promise.all([
        fetch(`/api/order/stores/${storeId}/checkout-upsells`, { headers: authHeaders() }),
        fetch(`/api/order/stores/${storeId}/menu-items`, { headers: authHeaders() }),
      ]);
      if (uRes.ok) setUpsells(((await uRes.json()) as { items: Upsell[] }).items ?? []);
      if (mRes.ok) setMenuItems(((await mRes.json()) as { items: MenuItem[] }).items ?? []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [storeId]);

  useEffect(() => { void load(); }, [load]);

  const configuredItemIds = new Set(upsells.map((u) => u.menuItemId));
  const pickableItems = menuItems.filter((m) => !configuredItemIds.has(m.id)).sort((a, b) => a.name.localeCompare(b.name));
  const selectedItem = pickItemId === "" ? null : menuItems.find((m) => m.id === pickItemId) ?? null;

  async function addUpsell() {
    if (pickItemId === "") { toast({ title: "Pick an item to feature", variant: "destructive" }); return; }
    const body: Record<string, unknown> = { menuItemId: pickItemId, offerType };
    if (offerType === "special_price") {
      const pricePence = Math.round(Number(priceInput) * 100);
      if (!Number.isFinite(pricePence) || pricePence < 0) { toast({ title: "Enter a valid special price", variant: "destructive" }); return; }
      if (selectedItem && pricePence > selectedItem.pricePence) { toast({ title: "Special price can't be higher than the normal price", variant: "destructive" }); return; }
      body["specialPricePence"] = pricePence;
    } else if (offerType === "percent") {
      const percentOff = Math.round(Number(percentInput));
      if (!Number.isInteger(percentOff) || percentOff < 1 || percentOff > 90) { toast({ title: "Enter a % between 1 and 90", variant: "destructive" }); return; }
      body["percentOff"] = percentOff;
    }
    // (standard price sends only offerType — no discount)
    setSaving(true);
    try {
      const res = await fetch(`/api/order/stores/${storeId}/checkout-upsells`, { method: "POST", headers: authHeaders(), body: JSON.stringify(body) });
      const d = await res.json().catch(() => ({})) as { error?: string };
      if (!res.ok) { toast({ title: d.error ?? "Could not add upsell", variant: "destructive" }); return; }
      // Optionally take the item off the normal menu so it's offered only at checkout.
      // (pickItemId is guaranteed a number here — guarded at the top of this function.)
      if (hideFromMenu) await setItemHidden(pickItemId, true);
      toast({ title: "Upsell added" });
      setAdding(false); setPickItemId(""); setPriceInput(""); setPercentInput(""); setOfferType("special_price"); setHideFromMenu(false);
      await load();
    } catch { toast({ title: "Network error", variant: "destructive" }); }
    finally { setSaving(false); }
  }

  // Toggle a menu item's hidden_from_menu flag (hidden = offered only as a checkout upsell).
  async function setItemHidden(menuItemId: number, hidden: boolean) {
    const res = await fetch(`/api/order/stores/${storeId}/menu-items/${menuItemId}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ hiddenFromMenu: hidden }) });
    if (!res.ok) { toast({ title: "Could not update menu visibility", variant: "destructive" }); return; }
    setMenuItems((prev) => prev.map((m) => m.id === menuItemId ? { ...m, hiddenFromMenu: hidden } : m));
  }

  async function toggleActive(u: Upsell) {
    try {
      const res = await fetch(`/api/order/stores/${storeId}/checkout-upsells/${u.id}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ active: !u.active }) });
      if (!res.ok) { toast({ title: "Could not update", variant: "destructive" }); return; }
      setUpsells((prev) => prev.map((x) => x.id === u.id ? { ...x, active: !x.active } : x));
    } catch { toast({ title: "Network error", variant: "destructive" }); }
  }

  async function remove(u: Upsell) {
    if (!confirm(`Remove "${u.name ?? "this item"}" from checkout upsells?`)) return;
    try {
      const res = await fetch(`/api/order/stores/${storeId}/checkout-upsells/${u.id}`, { method: "DELETE", headers: authHeaders() });
      if (!res.ok) { toast({ title: "Could not remove", variant: "destructive" }); return; }
      setUpsells((prev) => prev.filter((x) => x.id !== u.id));
    } catch { toast({ title: "Network error", variant: "destructive" }); }
  }

  function offerLabel(u: Upsell): string {
    if (u.offerType === "standard") return "Normal price";
    if (u.offerType === "percent" && u.percentOff != null) return `${u.percentOff}% off`;
    return `Special ${pence(u.specialPricePence ?? 0)}`;
  }

  const hiddenById = new Map(menuItems.map((m) => [m.id, m.hiddenFromMenu === true]));

  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-4">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={16} className="text-amber-600" />
        <h3 className="text-base font-bold text-slate-800">Checkout upsells</h3>
      </div>
      <p className="text-xs text-slate-500 mb-3">Shown to customers right before they pay — "Fancy any of these?". Feature your items at a special price to boost each order.</p>

      {loading ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : (
        <>
          {upsells.length === 0 && <p className="text-sm text-slate-400 mb-3">No upsells yet. Add one below.</p>}
          <div className="space-y-2 mb-3">
            {upsells.map((u) => (
              <div key={u.id} className="flex items-center gap-3 bg-white rounded-xl border border-slate-200 p-2">
                {u.imageUrl
                  ? <img src={u.imageUrl} alt={u.name ?? ""} className="w-12 h-12 rounded-lg object-cover shrink-0" />
                  : <div className="w-12 h-12 rounded-lg bg-slate-100 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{u.name ?? `Item #${u.menuItemId}`}</p>
                  <div className="flex items-center gap-2 text-xs flex-wrap">
                    <span className="font-bold text-amber-700">{u.offerPricePence != null ? pence(u.offerPricePence) : "—"}</span>
                    {u.normalPricePence != null && u.offerPricePence != null && u.offerPricePence < u.normalPricePence && (
                      <span className="text-slate-400 line-through">{pence(u.normalPricePence)}</span>
                    )}
                    <span className="text-slate-500">· {offerLabel(u)}</span>
                    {hiddenById.get(u.menuItemId) && <span className="text-indigo-600 font-medium">· off menu</span>}
                    {u.available === false && <span className="text-red-500">· out of stock</span>}
                  </div>
                </div>
                <label className="flex items-center gap-1 text-xs text-slate-600 shrink-0 cursor-pointer" title="Hide this item from the normal menu — offer it only here at checkout">
                  <input type="checkbox" checked={hiddenById.get(u.menuItemId) ?? false} onChange={(e) => void setItemHidden(u.menuItemId, e.target.checked)} className="w-4 h-4 accent-indigo-600" />
                  Off menu
                </label>
                <label className="flex items-center gap-1 text-xs text-slate-600 shrink-0 cursor-pointer">
                  <input type="checkbox" checked={u.active} onChange={() => void toggleActive(u)} className="w-4 h-4 accent-amber-600" />
                  Active
                </label>
                <button onClick={() => void remove(u)} className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:bg-red-50 shrink-0">
                  <Trash2 size={14} className="text-red-500" />
                </button>
              </div>
            ))}
          </div>

          {adding ? (
            <div className="bg-white rounded-xl border border-slate-200 p-3 space-y-3">
              <select
                value={pickItemId}
                onChange={(e) => setPickItemId(e.target.value === "" ? "" : Number(e.target.value))}
                className="w-full border border-input rounded-md px-2 py-2 text-sm bg-background"
              >
                <option value="">Choose an item to feature…</option>
                {pickableItems.map((m) => (
                  <option key={m.id} value={m.id}>{m.name} — {pence(m.pricePence)}</option>
                ))}
              </select>

              <div className="flex flex-wrap items-center gap-2">
                <select value={offerType} onChange={(e) => setOfferType(e.target.value as OfferType)} className="border border-input rounded-md px-2 py-2 text-sm bg-background">
                  <option value="special_price">Special price £</option>
                  <option value="percent">% off</option>
                  <option value="standard">Normal price (no discount)</option>
                </select>
                {offerType === "special_price" && (
                  <Input type="number" min={0} step={0.01} placeholder="e.g. 2.00" value={priceInput} onChange={(e) => setPriceInput(e.target.value)} className="w-28" />
                )}
                {offerType === "percent" && (
                  <Input type="number" min={1} max={90} step={1} placeholder="e.g. 20" value={percentInput} onChange={(e) => setPercentInput(e.target.value)} className="w-24" />
                )}
                {selectedItem && <span className="text-xs text-slate-400">normal {pence(selectedItem.pricePence)}</span>}
              </div>

              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                <input type="checkbox" checked={hideFromMenu} onChange={(e) => setHideFromMenu(e.target.checked)} className="w-4 h-4 accent-indigo-600" />
                Hide from the normal menu — offer this item only here at checkout ("last minute" item)
              </label>

              <div className="flex gap-2">
                <Button size="sm" onClick={() => void addUpsell()} disabled={saving}>{saving ? "Saving…" : "Save upsell"}</Button>
                <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setPickItemId(""); setPriceInput(""); setPercentInput(""); }}>Cancel</Button>
              </div>
            </div>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setAdding(true)} className="gap-1">
              <Plus size={14} /> Add upsell
            </Button>
          )}
        </>
      )}
    </div>
  );
}
